import React, { useState, useEffect, useRef } from 'react';
import Modal from './ui/Modal';
import BalancesModal from './BalancesModal';
import FeedbackModal from './FeedbackModal';
import SettingsOverlay from './SettingsOverlay';
import { centsToInputString } from '../utils/currency';
import { useCurrency } from '../utils/currencyContext';
import { awardPotsToWinners, calculatePlayerPositions, calculateSidePotsFromState, getMinimumRaise } from '../utils/pokerLogic';
import { updateGameState, updatePlayers, sendAndProcessAction, advanceToNextStage, endGameSession, sendWinnerCallout, undoLastAction, clearUndoSnapshot, moveChips, rebuyChips, requestRebuy, cancelRebuyRequest, addPendingRebuy, toStoredPlayer, removePlayer, transferHost, issueRejoinLink, sitOut, imBack, setAwayPlayersPayBlinds, getRoom, blindsGoUpDealFromSettings, writeDealtHand } from '../utils/roomManager';
import { ref } from 'firebase/database';
import { database } from '../firebase/firebaseConfig';
import { isDisconnected, isSeated, isShowdownEligible, parkedPlayers, seatedPlayers, canDealNextHand, dealBlockedByAway, isViewerHost, hasUnfinishedHandChips, nextConnectedHostId, isAway, isSitOutPending, holdsCardsThisHand, needsSitOutAutoAction, sitOutAutoAction, finalizeSitOutFlags, mergeLiveAwayFlags, isChipless, pendingRebuyList, tableDefaultBuyIn } from '../utils/playerLedger';
import { dealHandWithAwayPolicy, awayPlayersPayBlinds } from '../utils/awayBlinds';
import { SIT_OUT_COPY, sitOutBlindsConfirmLine } from '../utils/sitOutCopy';
import { potsForDisplay, potsForAward, clearParkedHandChips } from '../utils/potDisplay';
import { isTapWinnerMode, tapWinnerButtonLabel } from '../utils/tapWinner';
import { applyAwardedHand } from '../utils/streetAction';
import { trackHandComplete, trackRebuy, trackGameEnd, trackNewHand, trackSessionEnd } from '../utils/analytics';
import { playerNamesFrom } from '../utils/shouldCountStats';
import { getWinMessage, getCalloutText, pickCalloutIndex, joinWinnerNames, CALLOUT_MODES, DEFAULT_CALLOUT_MODE, DEFAULT_TABLE_VIBE } from '../utils/winMessages';
import { captureHandStarted, captureGameEnded, captureGamePlayerActive, captureRebuyRequested, captureRebuyAdded, captureRebuyCancelled, captureSpectateTapped, getGameId } from '../utils/posthogAnalytics';
import { getStoredGameLayout, setStoredGameLayout, GAME_LAYOUTS } from '../utils/sessionStore';
import { useScreenWakeLock, shouldHoldScreenWakeLock } from '../utils/screenWakeLock';
import { winnerInfoDismissKey, WinnerModalCard, WinnerOpponentNotice, HostHandResult, RaisePanel } from './game/GameParts';
import { RemovePlayerConfirm, RemovePlayerPicker } from './game/RemovePlayer';
import { AddOrRejoinSheet } from './game/AddOrRejoinSheet';
import ListLayout from './game/ListLayout';
import TableLayout from './game/TableLayout';
import { useActionSounds, useSoundMuted, useWinSound, useTurnSound } from '../utils/useActionSounds';
import { t } from '../i18n';
import { useLocale } from '../i18n/useT';
import { localizeError } from '../i18n/errors';
import { useVisualViewport } from '../utils/visualViewport';

function GameBoard({ gameState, currentPlayerId, onLeaveRoom }) {
  useLocale();
  const { formatCurrency, parseCurrencyInput, currency, locale, amountInputProps } = useCurrency();
  const { players, gameState: game, settings, roomCode, hostId, rebuyRequests } = gameState;
  // Convert players object to array and sort by joinedAt to maintain consistent seating order
  const allPlayersArray = Object.values(players || {}).sort((a, b) => (a.joinedAt || 0) - (b.joinedAt || 0));
  // Seated players occupy the table (a mid-game joiner is a "Next hand" seat).
  // Cashed-out and removed players stay in `players` for settlement only.
  const tablePlayers = allPlayersArray.filter(isSeated);
  const playersArray = allPlayersArray;

  // Host-ness comes from the live room, not the Home.jsx isHost flag. Joining
  // via the code/QR after a refresh used to set isHost:false even for the host,
  // which hid Deal next hand and Host tools. A leftover true flag
  // (or a missing hostId falling back to that prop) showed Host tools to a joiner.
  const isHost = isViewerHost(currentPlayerId, hostId, players);
  // Re-evaluated on every snapshot, so a rebuy re-enables Deal immediately.
  const dealable = canDealNextHand(players);
  const blockedByAway = dealBlockedByAway(players);
  const payAwayBlinds = awayPlayersPayBlinds(settings);
  // True while the host's Deal is in flight, so the action-slot fallback
  // doesn't flash in the gap between hiding the overlay and the new hand.
  const [isDealing, setIsDealing] = useState(false);
  const vv = useVisualViewport();
  const pinToKeyboard = vv.keyboardOpen;

  // PostHog per-player entry event inputs. Host state prefers the synced room
  // record (server truth, same basis as roomManager.verifyHost) over the
  // localStorage-restored isHost prop; currentPlayerId is the Firebase UID.
  const analyticsGameId = getGameId(gameState);
  const isHostViewer = isHost;
  
  // Winner declaration state
  const [showWinnerModal, setShowWinnerModal] = useState(false);
  const [selectedWinners, setSelectedWinners] = useState([]);
  const [isDeclaringWinner, setIsDeclaringWinner] = useState(false);
  
  // Player action state
  const [isPerformingAction, setIsPerformingAction] = useState(false);
  const [raiseAmount, setRaiseAmount] = useState(0);
  
  // Raise panel state
  const [showRaisePanel, setShowRaisePanel] = useState(false);
  
  // Winner animation state — driven by Firebase `gameState.winnerInfo` so that
  // every client (not just the host) sees the result of a hand.
  // winnerIds: array of winning player IDs (supports split pots).
  // winnerWinnings: map of playerId -> cents won (per-winner share).
  const [winnerIds, setWinnerIds] = useState([]);
  const [winnerWinnings, setWinnerWinnings] = useState({});
  // Remembers the winnerInfo key this client already dismissed, so a non-host
  // tapping "Got it" doesn't get the modal re-shown on the next Firebase snapshot.
  const dismissedWinnerRef = useRef(null);

  // Chip Manager state
  const [showRebuyModal, setShowRebuyModal] = useState(false);
  const [selectedPlayerId, setSelectedPlayerId] = useState('');
  const [chipAmount, setChipAmount] = useState('');
  const [isRebuying, setIsRebuying] = useState(false);
  // Sync lock: setState is too slow to catch a second tap in the same tick.
  const rebuyingRef = useRef(false);
  // Moving chips between players — a separate thing from a rebuy. A rebuy is cash
  // going onto the table; a move is the host fixing a mistake, where no cash
  // changed hands. Always a transfer, never a one-sided add or subtract: chips
  // only ever move between players, which is what keeps settle-up balanced.
  const [showMoveChipsModal, setShowMoveChipsModal] = useState(false);
  const [moveFromId, setMoveFromId] = useState('');
  const [moveToId, setMoveToId] = useState('');
  const [moveAmount, setMoveAmount] = useState('');
  const [isMovingChips, setIsMovingChips] = useState(false);
  // Sync lock: setState is too slow to catch a second tap in the same tick.
  const movingChipsRef = useRef(false);

  // One-level undo of the last gameplay action (host only).
  const [showUndoModal, setShowUndoModal] = useState(false);
  const [isUndoing, setIsUndoing] = useState(false);

  // End Game Confirmation state
  const [showEndGameModal, setShowEndGameModal] = useState(false);

  // Mid-game balances peek (read-only; does not end the game)
  const [showBalancesModal, setShowBalancesModal] = useState(false);

  // Change blinds — host only, from Host tools. Writes settings.smallBlind /
  // settings.bigBlind; the next Deal posts them.
  const [showChangeBlindsModal, setShowChangeBlindsModal] = useState(false);

  // Mid-game feedback — local modal only, never touches room state, so the
  // host dealing the next hand can't rip it away mid-typing.
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);

  // Chipless Player state
  const [showChiplessModal, setShowChiplessModal] = useState(false);

  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [showSitOutModal, setShowSitOutModal] = useState(false);
  const [rebuyAskOpen, setRebuyAskOpen] = useState(true);
  const [requestingRebuy, setRequestingRebuy] = useState(false);
  const [cancellingRebuy, setCancellingRebuy] = useState(false);
  const [addingRebuyId, setAddingRebuyId] = useState(null);
  const rebuyAskHandRef = useRef(null);
  const requestingRebuyRef = useRef(false);
  const cancellingRebuyRef = useRef(false);
  const addingRebuyRef = useRef(false);
  // Host tools → Remove player: pick a player, then "Remove Alex?".
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [removeTarget, setRemoveTarget] = useState(null);
  const [isRemoving, setIsRemoving] = useState(false);
  const [removeError, setRemoveError] = useState('');
  const [showAddOrRejoin, setShowAddOrRejoin] = useState(false);
  const [showHostTools, setShowHostTools] = useState(false);
  const [showMakeHostModal, setShowMakeHostModal] = useState(false);
  const [rejoinLink, setRejoinLink] = useState(null);
  const [rejoinError, setRejoinError] = useState('');

  // Which game layout THIS DEVICE is using — the oval table (default), or the
  // original list. Read from localStorage, written back on toggle; never room
  // state, so switching cannot affect another player's screen mid-hand.
  const [layout, setLayout] = useState(getStoredGameLayout);

  const handleToggleLayout = (next) => {
    const value = next === GAME_LAYOUTS.TABLE ? GAME_LAYOUTS.TABLE : GAME_LAYOUTS.LIST;
    setLayout(value);
    setStoredGameLayout(value);
  };

  useEffect(() => {
    if (isHost) return;
    setShowAddOrRejoin(false);
    setShowHostTools(false);
    setShowMakeHostModal(false);
    setRejoinLink(null);
    setShowRemoveModal(false);
    setRemoveTarget(null);
    setShowRebuyModal(false);
    setShowChangeBlindsModal(false);
    setShowMoveChipsModal(false);
    setShowUndoModal(false);
    setShowEndGameModal(false);
  }, [isHost]);


  useEffect(() => {
    window.scrollTo(0, 0);
    requestAnimationFrame(() => window.scrollTo(0, 1));
  }, []);

  // Keep the phone screen on from the first deal until Settle Up.
  // GameBoard only mounts while the room is PLAYING, so lobby / end / leave
  // unmount it and the lock releases. No UI — layouts stay untouched.
  useScreenWakeLock(shouldHoldScreenWakeLock(gameState.status));

  // PostHog: this player entered a live game — fires for host AND joiners,
  // once per game per device (the wrapper dedups refresh/rejoin remounts and
  // awaits identify so the event lands on the Firebase UID, not a throwaway
  // anonymous id). Deps are the derived scalars, not `gameState`, which is
  // replaced on every Firebase snapshot.
  useEffect(() => {
    if (!analyticsGameId) return;
    captureGamePlayerActive(gameState, isHostViewer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analyticsGameId, isHostViewer]);

  
  // Initialize stage and hand number first (needed for pot calculation)
  const currentStage = game?.stage || 'PRE_FLOP';
  const handNumber = game?.handNumber || 1;

  // 🎯 NEW POT SYSTEM - Single source of truth with totalPotContribution
  // SIMPLE CALCULATION: Just sum up all totalPotContribution fields
  const totalPot = playersArray.reduce((sum, p) => {
    const contribution = p.totalPotContribution || 0;
    return sum + contribution;
  }, 0);
  
  console.log('💰 SIMPLE POT CALCULATION:', {
    stage: currentStage,
    handNumber: handNumber,
    totalPot: totalPot,
    totalPotDollars: formatCurrency(totalPot),
    playerContributions: playersArray.map(p => ({
      name: p.name,
      totalPotContribution: p.totalPotContribution || 0,
      currentRoundBet: p.currentRoundBet || 0
    }))
  });
  
  // Calculate side pots for proper display using new system
  const rawPots = game?.pots && game.pots.length > 0 ? game.pots : calculateSidePotsFromState({ players: playersArray });
  const currentPots = potsForDisplay(rawPots, playersArray);

  // No complex debugging needed with simple pot system
  
  // Calculate current bet to call (highest current round bet)
  const highestBet = Math.max(...playersArray.map(p => p.currentRoundBet || 0), 0);
  const currentPlayer = playersArray.find(p => p.id === currentPlayerId);
  const pendingRebuys = pendingRebuyList(rebuyRequests, players);
  const defaultBuyInCents = tableDefaultBuyIn(players, hostId);
  const defaultBuyInLabel = centsToInputString(defaultBuyInCents, currency, locale);
  const myRebuyRequest = pendingRebuys.find((r) => r.playerId === currentPlayerId) || null;

  // Chips restore the ask form. A new hand dealt around a $0 seat (Spectate
  // or just sitting there) drops it so the live table shows the pill, not
  // action buttons. Hand-number is the deal signal — tapping Rebuy on the
  // pill mid-hand must not get snapped shut.
  useEffect(() => {
    if ((currentPlayer?.stack || 0) > 0) {
      setRebuyAskOpen(true);
      rebuyAskHandRef.current = game?.handNumber;
      return;
    }
    if (rebuyAskHandRef.current == null) {
      rebuyAskHandRef.current = game?.handNumber;
      return;
    }
    if (game?.handNumber !== rebuyAskHandRef.current) {
      rebuyAskHandRef.current = game?.handNumber;
      setRebuyAskOpen(false);
    }
  }, [game?.handNumber, currentPlayer?.stack]);
  const toCall = Math.max(0, highestBet - (currentPlayer?.currentRoundBet || 0));

  // Seated, not folded, isActive !== false — omitted isActive after a
  // refresh still counts, so tap-the-winner / Declare Winner can open.
  const eligiblePlayers = tablePlayers.filter(isShowdownEligible);

  // Winner declaration logic
  const canManuallyDeclareWinner = currentStage === 'SHOWDOWN' && eligiblePlayers.length > 1 && totalPot > 0;

  // After manual winner declaration, pot should be 0, so canDeclareWinner should be false
  const canDeclareWinner = canManuallyDeclareWinner && totalPot > 0;

  // One pot at showdown: host taps seats on the board. Side pots keep the
  // Declare Winner pop-up (each pot can have a different winner).
  const tapWinnerMode = isTapWinnerMode({
    isHost,
    stage: currentStage,
    totalPot,
    eligibleCount: eligiblePlayers.length,
    players: tablePlayers,
  });
  const awardLabel = tapWinnerButtonLabel({
    selectedIds: selectedWinners,
    players: tablePlayers,
    potCents: totalPot,
    formatCurrency,
  });

  // Winner animation is driven entirely by Firebase `gameState.winnerInfo`, which
  // the host writes when a hand ends (by fold in roomManager.processAction, or by
  // manual showdown declaration in handleConfirmWinner). Every client reads it here,
  // so opponents see the result too — not just the host.
  const winnerInfo = game?.winnerInfo;
  useEffect(() => {
    if (!winnerInfo || !Array.isArray(winnerInfo.winnerIds) || winnerInfo.winnerIds.length === 0) {
      setWinnerIds([]);
      setWinnerWinnings({});
      return;
    }
    const key = winnerInfoDismissKey(winnerInfo);
    if (dismissedWinnerRef.current === key) return; // already dismissed on this client
    setWinnerIds(winnerInfo.winnerIds);
    setWinnerWinnings(winnerInfo.winnerWinnings || {});
  }, [winnerInfo]);

  // Seat picks are local to this phone. A refresh (or leaving showdown / going
  // to the side-pot pop-up) drops them — they are never written to Firebase.
  useEffect(() => {
    if (!tapWinnerMode && !showWinnerModal) {
      setSelectedWinners((prev) => (prev.length === 0 ? prev : []));
    }
  }, [tapWinnerMode, showWinnerModal]);

  // All actions taken so far in the current betting round (street). Written by
  // roomManager.processAction as an append-only log; cleared each time the
  // street advances or a new hand starts, so it never spans more than one round.
  const roundActions = game?.roundActions || [];

  // Check / raise / win / your-turn sounds (fold and call are silent), from synced state so every phone hears
  // each action once. Mute is per phone.
  const [soundMuted, toggleSoundMuted] = useSoundMuted();
  useActionSounds(game?.lastAction, soundMuted);
  useWinSound(game?.winnerInfo, soundMuted);
  useTurnSound({
    turnId: game?.currentTurnPlayerId || null,
    myId: currentPlayerId,
    handNumber: game?.handNumber,
    stage: game?.stage,
  }, soundMuted);


  // Check/Fold pre-action. Armed per street (`hand-stage`), so it resets on
  // every street and new hand. When the turn arrives: check if nothing is owed,
  // fold if there's a bet. Purely local to this phone; nothing is written until
  // the action fires, and it fires through the normal action path.
  const streetKey = `${handNumber}-${currentStage}`;
  const [checkFoldArmedKey, setCheckFoldArmedKey] = useState(null);
  const checkFoldArmed = checkFoldArmedKey === streetKey;
  const onToggleCheckFold = () => setCheckFoldArmedKey(checkFoldArmed ? null : streetKey);
  const checkFoldFiringRef = useRef(false);
  useEffect(() => {
    if (!checkFoldArmed || !currentPlayer || isPerformingAction) return;
    if (game?.currentTurnPlayerId !== currentPlayerId) return;
    if (checkFoldFiringRef.current) return;
    checkFoldFiringRef.current = true;
    setCheckFoldArmedKey(null);
    setIsPerformingAction(true);
    const action = toCall === 0 ? { type: 'CALL', auto: true } : { type: 'FOLD', auto: true };
    sendAndProcessAction(roomCode, currentPlayerId, action, game, playersArray, settings, hostId)
      .catch((error) => console.error('Failed auto Check/Fold:', error))
      .finally(() => { checkFoldFiringRef.current = false; setIsPerformingAction(false); });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkFoldArmed, game?.currentTurnPlayerId, currentPlayerId, isPerformingAction]);

  // Sit out / Away auto check-or-fold. Any client fires so the table does not
  // wait on a pocketed phone. Same CALL/FOLD as Check/Fold; last write wins.
  // Read the latest snapshot from a ref so a null processAction (stale turn)
  // retries with fresh state instead of latching a "done" key. Viewer
  // isPerformingAction is unrelated — any phone can fire this.
  const sitOutAutoSnapRef = useRef({});
  sitOutAutoSnapRef.current = { game, players, playersArray, tablePlayers, settings, hostId, roomCode };
  const sitOutAutoInFlightRef = useRef(false);
  useEffect(() => {
    const turnId = game?.currentTurnPlayerId;
    if (!turnId || game?.winnerInfo) return undefined;
    const actor = players?.[turnId];
    if (!needsSitOutAutoAction(actor)) return undefined;

    let cancelled = false;
    let attempts = 0;
    const attempt = () => {
      if (cancelled || sitOutAutoInFlightRef.current) return;
      const snap = sitOutAutoSnapRef.current;
      const latestTurn = snap.game?.currentTurnPlayerId;
      if (!latestTurn || snap.game?.winnerInfo) return;
      const latest = snap.players?.[latestTurn];
      if (!needsSitOutAutoAction(latest)) return;
      sitOutAutoInFlightRef.current = true;
      attempts += 1;
      const highest = Math.max(0, ...(snap.tablePlayers || []).map((p) => p.currentRoundBet || 0));
      const owed = Math.max(0, highest - (latest.currentRoundBet || 0));
      sendAndProcessAction(
        snap.roomCode,
        latestTurn,
        sitOutAutoAction(owed),
        snap.game,
        snap.playersArray,
        snap.settings,
        snap.hostId
      )
        .then((result) => {
          if (result) return;
          sitOutAutoInFlightRef.current = false;
          if (!cancelled && attempts < 12) setTimeout(attempt, 250);
        })
        .catch((error) => {
          sitOutAutoInFlightRef.current = false;
          console.error('Failed sit-out auto action:', error);
          if (!cancelled && attempts < 12) setTimeout(attempt, 400);
        });
    };

    const t = setTimeout(attempt, 40);
    return () => {
      cancelled = true;
      clearTimeout(t);
      sitOutAutoInFlightRef.current = false;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [game?.currentTurnPlayerId, handNumber, currentStage, players?.[game?.currentTurnPlayerId]?.hasFolded]);

  // Player action handlers
  const handleFold = async () => {
    if (!currentPlayer || isPerformingAction || game?.currentTurnPlayerId !== currentPlayerId) return;
    
    setIsPerformingAction(true);
    try {
      await sendAndProcessAction(roomCode, currentPlayerId, { type: 'FOLD' }, game, playersArray, settings, hostId);
      console.log('🎮 Fold action processed');
    } catch (error) {
      console.error('Failed to fold:', error);
    } finally {
      setIsPerformingAction(false);
    }
  };

  const handleCall = async () => {
    if (!currentPlayer || isPerformingAction || game?.currentTurnPlayerId !== currentPlayerId) return;
    
    setIsPerformingAction(true);
    try {
      await sendAndProcessAction(roomCode, currentPlayerId, { type: 'CALL' }, game, playersArray, settings, hostId);
      console.log('🎮 Call action processed');
    } catch (error) {
      console.error('Failed to call:', error);
    } finally {
      setIsPerformingAction(false);
    }
  };

  const handleRaise = async (totalBetAmount) => {
    if (!currentPlayer || isPerformingAction || game?.currentTurnPlayerId !== currentPlayerId) return;
    
    setIsPerformingAction(true);
    try {
      await sendAndProcessAction(roomCode, currentPlayerId, { type: 'RAISE', amount: totalBetAmount }, game, playersArray, settings, hostId);
      console.log('🎮 Raise action processed - total bet:', totalBetAmount);
      setShowRaisePanel(false);
    } catch (error) {
      console.error('Failed to raise:', error);
    } finally {
      setIsPerformingAction(false);
    }
  };

  const handleAllIn = async () => {
    if (!currentPlayer || isPerformingAction || game?.currentTurnPlayerId !== currentPlayerId) return;
    
    setIsPerformingAction(true);
    try {
      await sendAndProcessAction(roomCode, currentPlayerId, { type: 'ALL_IN' }, game, playersArray, settings, hostId);
      console.log('🎮 All-in action processed');
    } catch (error) {
      console.error('Failed to all-in:', error);
    } finally {
      setIsPerformingAction(false);
    }
  };

  // Raise panel handlers
  const handleShowRaisePanel = () => {
    setShowRaisePanel(true);
  };

  const handleCloseRaisePanel = () => {
    setShowRaisePanel(false);
  };

  const handleConfirmRaise = (totalBetInCents) => handleRaise(totalBetInCents);

  const handlePresetRaise = (amount) => {
    handleRaise(amount);
  };

  // End game handlers
  const handleEndGame = () => {
    if (!isHost) return;
    setShowEndGameModal(true);
  };

  const confirmEndGame = async () => {
    if (!isHost) return;
    
    try {
      const gameEndTime = Date.now();
      const gameStartTime = gameState?.createdAt || gameEndTime; // Fallback to current time if no createdAt
      const gameDuration = gameEndTime - gameStartTime;
      
      // Update Firebase game state to trigger EndGameScreen for all players
      const updatedGameState = {
        ...game,
        status: 'ENDED', // Mark game as ended
        endedAt: gameEndTime,
        endedBy: currentPlayerId
      };
      
      await endGameSession(roomCode, updatedGameState, currentPlayerId);

      // 🔥 ANALYTICS: Track game end event (host only)
      // Triggers when the host ends the game and settlements are finalized - increments games_ended counter
      trackGameEnd(isHost, playerNamesFrom(players));
      // PostHog: games ended (stat #2). Host End Game settle only — not auto-end.
      captureGameEnded(gameState, game?.handNumber ?? 0, 'host');

      // 🔥 ANALYTICS: Track game session end (host only)
      // Ends timing the actual poker game session - calculates total PLAYING time
      if (isHost) {
        trackSessionEnd(playerNamesFrom(players));
      }

      console.log('🏁 Game ended by host');
      setShowEndGameModal(false);
    } catch (error) {
      console.error('Failed to end game:', error);
    }
  };


  const handleBackToHome = () => {
    onLeaveRoom();
  };

  const nextHostId = isHost ? nextConnectedHostId(players, hostId) : null;
  const nextHost = nextHostId ? players?.[nextHostId] : null;

  // Leave may open while a successor is still seated; if they cash out before
  // confirm, fall through to End & Settle instead of handing host to nobody.
  useEffect(() => {
    if (!showLeaveModal || !isHost || nextHostId) return;
    setShowLeaveModal(false);
    setShowEndGameModal(true);
  }, [showLeaveModal, isHost, nextHostId]);

  const canSitOut = isSeated(currentPlayer) && !isAway(currentPlayer) && !isSitOutPending(currentPlayer);

  const handleSitOut = async () => {
    setShowSitOutModal(false);
    try {
      await sitOut(roomCode, currentPlayerId);
    } catch (error) {
      console.error('Failed to sit out:', error);
    }
  };

  const handleImBack = async () => {
    try {
      await imBack(roomCode, currentPlayerId);
    } catch (error) {
      console.error('Failed to come back:', error);
    }
  };

  const handleToggleAwayPlayersPayBlinds = async (enabled) => {
    if (!isHost) return;
    try {
      await setAwayPlayersPayBlinds(roomCode, currentPlayerId, enabled);
    } catch (error) {
      console.error('Failed to update away-blinds setting:', error);
    }
  };

  const handleLeaveTable = () => {
    if (isHost && !nextHost) {
      setShowEndGameModal(true);
      return;
    }
    setShowLeaveModal(true);
  };

  const handleConfirmLeaveTable = () => {
    if (isHost && !nextConnectedHostId(players, hostId)) {
      setShowLeaveModal(false);
      setShowEndGameModal(true);
      return;
    }
    setShowLeaveModal(false);
    onLeaveRoom();
  };

  // Everyone at the table except the host. A "Next hand" seat counts.
  const removablePlayers = tablePlayers.filter((p) => p.id !== hostId);
  const droppedPlayers = tablePlayers.filter(isDisconnected);

  const handlePickRejoin = async (player) => {
    if (!isHost || !player?.id) return;
    if (rejoinLink?.player?.id === player.id) {
      setRejoinLink(null);
      return;
    }
    setRejoinError('');
    try {
      const issued = await issueRejoinLink(roomCode, player.id, currentPlayerId);
      setRejoinLink({ player, url: issued.url });
    } catch (error) {
      console.error('Failed to issue rejoin link:', error);
      setRejoinError(error.message || t('error.couldNotRejoin'));
    }
  };

  const handleTransferHost = async (player) => {
    if (!isHost || !player?.id) return;
    try {
      await transferHost(roomCode, player.id, currentPlayerId);
      setShowMakeHostModal(false);
      setShowHostTools(false);
    } catch (error) {
      console.error('Failed to transfer host:', error);
    }
  };

  const handlePickRemove = (player) => {
    setRemoveError('');
    setRemoveTarget(player);
  };

  const handleCancelRemove = () => {
    if (isRemoving) return;
    setRemoveTarget(null);
    setRemoveError('');
  };

  // Same cash-out as a leave (roomManager.planRemovePlayer): folded out of
  // the hand with their pot chips left in, stack frozen for who-owes-who.
  const handleConfirmRemovePlayer = async () => {
    if (!isHost || !removeTarget || isRemoving) return;
    setIsRemoving(true);
    setRemoveError('');
    try {
      await removePlayer(roomCode, removeTarget.id, currentPlayerId);
      setRemoveTarget(null);
      setShowRemoveModal(false);
    } catch (error) {
      console.error('Failed to remove player:', error);
      setRemoveError(error.message || t('error.couldNotRemove'));
    } finally {
      setIsRemoving(false);
    }
  };

  // Check if game should end automatically or if host has ended it
  const gameEnded = game?.status === 'ENDED';
  // Only auto-end if there's truly only 1 player total in the game (others left/disconnected)
  const cashedOutPlayers = allPlayersArray.filter((p) => p.status === 'left');
  // Auto-end only when the table is down to one seated player and nobody
  // cashed out a seat they might reclaim. A rejoin sits out the current hand
  // (isActive false) — that must not look like "only one player left".
  const autoEndGame = currentStage === 'SHOWDOWN' && tablePlayers.length <= 1 && cashedOutPlayers.length === 0;

  // Auto-end game when only one player remains (host only)
  useEffect(() => {
    if (autoEndGame && !gameEnded && isHost) {
      const handleAutoEndGame = async () => {
        try {
          const gameEndTime = Date.now();
          const gameStartTime = gameState?.createdAt || gameEndTime;
          const gameDuration = gameEndTime - gameStartTime;
          
          const updatedGameState = {
            ...game,
            status: 'ENDED',
            endedAt: gameEndTime,
            endedBy: 'auto'
          };
          
          await endGameSession(roomCode, updatedGameState, currentPlayerId);

          // 🔥 ANALYTICS: Track auto game end event (host only)
          // Triggers when game auto-ends due to only one player remaining - increments games_ended counter
          trackGameEnd(isHost, playerNamesFrom(players));

          // 🔥 ANALYTICS: Track game session end (host only)
          // Ends timing the actual poker game session - calculates total PLAYING time
          if (isHost) {
            trackSessionEnd(playerNamesFrom(players));
          }

          console.log('🏁 Game auto-ended - only one player remaining');
        } catch (error) {
          console.error('Failed to auto-end game:', error);
        }
      };
      
      handleAutoEndGame();
    }
  }, [autoEndGame, gameEnded, isHost, game, roomCode, currentPlayerId]);

  // Show chipless modal for players with $0 when it's their turn
  useEffect(() => {
    const isMyTurn = game?.currentTurnPlayerId === currentPlayerId;
    const isChipless = currentPlayer && currentPlayer.stack === 0;
    
    if (isMyTurn && isChipless && !showChiplessModal) {
      setShowChiplessModal(true);
      // Auto-dismiss after 5 seconds to be less intrusive
      const timer = setTimeout(() => {
        setShowChiplessModal(false);
      }, 5000);
      return () => clearTimeout(timer);
    } else if (!isMyTurn || !isChipless) {
      setShowChiplessModal(false);
    }
  }, [game?.currentTurnPlayerId, currentPlayerId, currentPlayer?.stack, showChiplessModal]);

// Calculate raise amounts
  const minRaise = getMinimumRaise(playersArray, settings?.bigBlind || 200, game);
  const potRaise = totalPot;
  const allInAmount = currentPlayer?.stack || 0;

  // Host control handlers
  const handleAdvanceStage = async () => {
    if (!isHost || !game?.canAdvanceStage) return;
    
    try {
      await advanceToNextStage(roomCode, currentPlayerId);
      console.log(`🎮 Stage advanced by host`);
    } catch (error) {
      console.error('Failed to advance stage:', error);
    }
  };

  const handleDeclareWinner = () => {
    if (!isHost) return;
    setShowWinnerModal(true);
    setSelectedWinners([]);
  };

  const closeWinnerModal = () => {
    setShowWinnerModal(false);
    setSelectedWinners([]);
  };

  // Same math the list preview uses, so the amounts on selected rows are the
  // amounts that get written. A wrong winner can be fixed with Move chips.
  const buildWinnerAward = (winnerIds) => {
    // Never award the display pots. Unmatched blinds collapse on screen into
    // one number; paying that number would hand the uncalled bet to the winner.
    // Return uncalled chips first, then award the engine pots.
    const { pots, players: settled } = potsForAward(playersArray);
    const potsToAward = pots && pots.length > 0 ? pots : calculateSidePotsFromState({ players: settled });
    const playerPositions = calculatePlayerPositions(settled, game.dealerId);
    return awardPotsToWinners(potsToAward, winnerIds, settled, playerPositions);
  };

  const handleConfirmWinner = async () => {
    if (!isHost || selectedWinners.length === 0) return;

    setIsDeclaringWinner(true);

    try {
      const result = buildWinnerAward(selectedWinners);

      // Drop the pending undo before awarding. A snapshot from earlier in the
      // hand predates this award, so applying it later would roll stacks back
      // and silently un-award a pot the host just confirmed. Cleared first so a
      // failure here aborts rather than leaving that trap armed.
      await clearUndoSnapshot(roomCode);

      // Same post-award write as fold-win in processAction: chips already in
      // stacks, contributions / street bets / roundActions cleared so seat
      // lines and Balances cannot keep leftover Bet $ amounts on a $0 pot.
      const awarded = applyAwardedHand(result.updatedPlayers, game, result, {
        winnerIds: selectedWinners,
        winnerWinnings: result.playerWinnings || {},
        // Chips handed back rather than won — normally empty, since an uncalled
        // bet is already returned when the hand locks. Non-empty only if this
        // award ran on state that never went through that path.
        refunds: result.refunds || {},
        handNumber: handNumber,
        ts: Date.now()
      });

      let awardedPlayers = finalizeSitOutFlags(awarded.players, awarded.gameState);
      let livePlayers = null;
      try {
        const liveRoom = await getRoom(roomCode);
        if (liveRoom?.players) {
          livePlayers = liveRoom.players;
          awardedPlayers = mergeLiveAwayFlags(awardedPlayers, livePlayers);
        }
      } catch (liveError) {
        console.warn('Award using local Away flags; live room read failed:', liveError);
      }
      const playersObject = {};
      awardedPlayers.forEach(player => {
        playersObject[player.id] = toStoredPlayer(player);
      });
      parkedPlayers(players).forEach((player) => {
        if (!playersObject[player.id]) {
          playersObject[player.id] = toStoredPlayer(clearParkedHandChips(player));
        }
      });
      
      await updatePlayers(roomCode, playersObject, currentPlayerId, false, {
        awayFlags: 'actor',
        livePlayers,
      });

      // `winnerInfo` is read by every client's effect to show the winner modal —
      // the modal stays open until the host taps "Deal next hand" (no auto-dismiss).
      await updateGameState(roomCode, awarded.gameState, currentPlayerId);

      // 🔥 ANALYTICS: Track hand completion event (host only)
      // Triggers when a pot is awarded after showdown or fold-out - increments hands_completed counter
      trackHandComplete(isHost);

      console.log('🏆 Winner declared successfully:', selectedWinners);
      console.log('🏆 Winnings breakdown:', result.playerWinnings);
      closeWinnerModal();
    } catch (error) {
      console.error('Failed to declare winner:', error);
    } finally {
      setIsDeclaringWinner(false);
    }
  };

  // Resolves true only when the new hand was written. Callers that hid the
  // hand-result overlay optimistically use false to bring it back.
  const handleNewHand = async () => {
    if (!isHost) return false;
    // Two seated players need chips (a busted player waits on a rebuy).
    // Without this the deal throws inside assignRoles and nothing happens.
    if (!dealable) return false;
    
    // Prevent double-execution with a simple debounce check
    if (handleNewHand.isExecuting) {
      console.warn('🎮 handleNewHand already executing, skipping duplicate call');
      return false;
    }
    handleNewHand.isExecuting = true;
    
    try {
      console.log('🎮 Starting new hand...', { currentHand: handNumber, dealerId: game.dealerId });

      // Step 1: Reset roles and player states for seated players only.
      // Cashed-out / removed players are merged back so a new-hand write
      // cannot drop them from settlement.
      let seated = seatedPlayers(players);
      let parked = parkedPlayers(players);
      let dealerIdNow = game.dealerId;
      let stageNow = currentStage;
      let turnNow = game.currentTurnPlayerId;
      let handNow = handNumber;
      let potsNow = game.pots;
      // Live RTDB true wins; if the key is missing, keep the UI value so a
      // lobby/host toggle is not treated as off just because get() omitted it.
      let payBlindsNow = payAwayBlinds || awayPlayersPayBlinds(settings);
      let liveSettings = settings;
      try {
        const liveRoom = await getRoom(roomCode);
        if (liveRoom?.players) {
          seated = seatedPlayers(liveRoom.players);
          parked = parkedPlayers(liveRoom.players);
        }
        if (liveRoom?.gameState) {
          dealerIdNow = liveRoom.gameState.dealerId || dealerIdNow;
          stageNow = liveRoom.gameState.stage || stageNow;
          turnNow = liveRoom.gameState.currentTurnPlayerId ?? turnNow;
          handNow = liveRoom.gameState.handNumber || handNow;
          potsNow = liveRoom.gameState.pots || potsNow;
        }
        if (liveRoom?.settings) liveSettings = liveRoom.settings;
        payBlindsNow = payAwayBlinds
          || awayPlayersPayBlinds(settings)
          || awayPlayersPayBlinds(liveRoom?.settings);
      } catch (liveError) {
        console.warn('Deal using local seats; live room read failed:', liveError);
      }
      const goUp = blindsGoUpDealFromSettings(liveSettings);
      const smallBlindAmount = goUp.smallBlind;
      const bigBlindAmount = goUp.bigBlind;
      const dealt = dealHandWithAwayPolicy({
        seated,
        dealerId: dealerIdNow,
        stage: stageNow,
        currentTurnPlayerId: turnNow,
        handNumber: handNow,
        pots: potsNow,
        smallBlind: smallBlindAmount,
        bigBlind: bigBlindAmount,
        payBlinds: payBlindsNow,
      });
      const { players: playersWithBlinds, gameStateData, firstToAct } = dealt;
      console.log('🎮 First to act:', firstToAct, { payAwayBlinds });
      
      // Step 4: Create clean game state with proper turn assignment
      const cleanGameState = {
        stage: 'PRE_FLOP',
        dealerId: gameStateData.dealerId,
        currentTurnPlayerId: firstToAct,
        handNumber: (gameStateData.handNumber || handNumber) + 1,
        pots: gameStateData.pots || [],
        canAdvanceStage: false,
        nextStage: null,
        nextStageName: null,
        stageProgress: 0,
        lastRaiseAmount: 0, // Initialize for new hand
        incompleteRaise: false,
        roundActions: [],   // New hand starts a fresh action log
        winnerInfo: null    // Clear winner modal for all clients
      };
      
      // Step 5: Convert players array to Firebase object format
      const playersObject = {};
      playersWithBlinds.forEach(player => {
        playersObject[player.id] = toStoredPlayer(player);
      });
      parked.forEach(player => {
        playersObject[player.id] = toStoredPlayer(clearParkedHandChips(player));
      });
      
      // Step 6: Update Firebase with new game state and players.
      // The previous hand's undo is obsolete the moment a new hand is dealt.
      // One atomic write (see writeDealtHand): no phone may ever see the new
      // hand's turn on top of the last hand's players.
      await writeDealtHand(roomCode, currentPlayerId, {
        gameState: cleanGameState,
        players: playersObject,
        extraUpdates: goUp.updates || {},
      });
      
      // 🔥 ANALYTICS: Track new hand (host only)
      // Increments total_hands_dealt counter for home page display
      trackNewHand(isHost, playerNamesFrom([...playersWithBlinds, ...parked]));
      // PostHog pilot metric: hands dealt. gameState prop is the full synced
      // room object, so it carries roomCode + createdAt for the game_id.
      captureHandStarted(gameState, cleanGameState.handNumber, playersWithBlinds.filter(p => p.isActive).length);

      console.log(`🎮 Started new hand #${cleanGameState.handNumber} - ${firstToAct} to act`);
      return true;
    } catch (error) {
      console.error('Failed to start new hand:', error);
      console.error('Error details:', error.message);
      return false;
    } finally {
      // Always reset the execution flag
      handleNewHand.isExecuting = false;
    }
  };

  const toggleWinnerSelection = (playerId) => {
    setSelectedWinners(prev => {
      if (prev.includes(playerId)) {
        return prev.filter(id => id !== playerId);
      } else {
        return [...prev, playerId];
      }
    });
  };

  // Chip Manager handlers
  const closeRebuyModal = () => {
    setShowRebuyModal(false);
    setSelectedPlayerId('');
    setChipAmount('');
  };

  const handleRebuy = async () => {
    if (!isHost || !selectedPlayerId || !chipAmount || rebuyingRef.current) return;

    let rebuyAmountInCents;
    try {
      rebuyAmountInCents = parseCurrencyInput(chipAmount);
    } catch {
      alert(t('error.validAmountExample'));
      return;
    }
    if (rebuyAmountInCents <= 0) {
      alert(t('error.positiveAmount'));
      return;
    }

    rebuyingRef.current = true;
    setIsRebuying(true);
    try {
      const outcome = await rebuyChips(roomCode, currentPlayerId, {
        playerId: selectedPlayerId,
        amount: rebuyAmountInCents
      });
      if (!outcome.ok) {
        alert(localizeError(outcome.reason));
        return;
      }

      // 🔥 ANALYTICS: Track rebuy event (host only)
      // Triggers when a player adds more chips mid-game - increments rebuys_processed counter
      trackRebuy(isHost, playerNamesFrom(players));
      captureRebuyAdded(gameState, rebuyAmountInCents);

      closeRebuyModal();
      console.log(`💰 Rebuy processed: +${formatCurrency(rebuyAmountInCents)} for ${outcome.name}`);
    } catch (error) {
      console.error('Failed to process rebuy:', error);
      alert(t('error.couldNotRebuy'));
    } finally {
      rebuyingRef.current = false;
      setIsRebuying(false);
    }
  };

  const handleRequestRebuy = async (amountCents) => {
    if (!currentPlayerId || requestingRebuyRef.current) return;
    requestingRebuyRef.current = true;
    setRequestingRebuy(true);
    try {
      const outcome = await requestRebuy(roomCode, currentPlayerId, amountCents);
      if (!outcome.ok) {
        alert(localizeError(outcome.reason));
        return;
      }
      captureRebuyRequested(gameState, amountCents);
    } catch (error) {
      console.error('Failed to request rebuy:', error);
      alert(t('error.couldNotRebuy'));
    } finally {
      requestingRebuyRef.current = false;
      setRequestingRebuy(false);
    }
  };

  const handleCancelRebuy = async () => {
    if (!currentPlayerId || cancellingRebuyRef.current) return;
    cancellingRebuyRef.current = true;
    setCancellingRebuy(true);
    try {
      const outcome = await cancelRebuyRequest(roomCode, currentPlayerId);
      if (outcome && outcome.ok === false) {
        alert(localizeError(outcome.reason));
        return;
      }
      captureRebuyCancelled(gameState);
    } catch (error) {
      console.error('Failed to cancel rebuy:', error);
    } finally {
      cancellingRebuyRef.current = false;
      setCancellingRebuy(false);
    }
  };

  const handleAddPendingRebuy = async (playerId) => {
    if (!isHost || !playerId || addingRebuyRef.current) return;
    addingRebuyRef.current = true;
    setAddingRebuyId(playerId);
    try {
      const outcome = await addPendingRebuy(roomCode, currentPlayerId, playerId);
      if (!outcome.ok) {
        alert(localizeError(outcome.reason));
        return;
      }
      trackRebuy(isHost, playerNamesFrom(players));
      captureRebuyAdded(gameState, outcome.amount);
    } catch (error) {
      console.error('Failed to add pending rebuy:', error);
      alert(t('error.couldNotRebuy'));
    } finally {
      addingRebuyRef.current = false;
      setAddingRebuyId(null);
    }
  };

  const handleSpectate = () => {
    setRebuyAskOpen(false);
    captureSpectateTapped(gameState);
  };

  const closeMoveChipsModal = () => {
    setShowMoveChipsModal(false);
    setMoveFromId('');
    setMoveToId('');
    setMoveAmount('');
  };

  // Host fixing a mistake — a pot that went to the wrong player, a wrong bet, a
  // misfold. Always a transfer between two players, which matters for more than
  // tidiness: settlement is stack - totalInvestment, and it only balances while
  // the chips on the table stay equal to the cash paid in. A one-sided change
  // breaks that, leaving winnings nobody is owed by. `totalInvestment` is
  // untouched here because no cash changed hands.
  const handleMoveChips = async () => {
    if (!isHost || !moveFromId || !moveToId || !moveAmount || movingChipsRef.current) return;

    let amountInCents;
    try {
      amountInCents = parseCurrencyInput(moveAmount);
    } catch {
      alert(t('error.validAmountExample'));
      return;
    }
    if (amountInCents <= 0) {
      alert(t('error.positiveAmount'));
      return;
    }

    movingChipsRef.current = true;
    setIsMovingChips(true);
    try {
      const outcome = await moveChips(roomCode, currentPlayerId, {
        fromId: moveFromId,
        toId: moveToId,
        amount: amountInCents
      });
      if (!outcome.ok) {
        alert(localizeError(outcome.reason));
        return;
      }
      closeMoveChipsModal();
      console.log(`🔀 Moved ${formatCurrency(amountInCents)} from ${outcome.fromName} to ${outcome.toName}`);
    } catch (error) {
      console.error('Failed to move chips:', error);
      alert(t('error.couldNotMove'));
    } finally {
      movingChipsRef.current = false;
      setIsMovingChips(false);
    }
  };



  // Winner modal CTA: host deals the next hand (which clears winnerInfo in Firebase
  // for everyone); a non-host just dismisses locally and records the key so the next
  // Firebase snapshot doesn't re-open the modal. Because the key includes the
  // callout timestamp, a callout arriving AFTER dismissal changes the key and
  // re-opens the modal so nobody misses it; dismissing again then sticks.
  //
  // Host: the overlay is the only Deal button, so it must never be dismissed
  // without a new hand actually starting. A heads-up bust used to hide it, the
  // deal threw (one player with chips), and after a rebuy there was no way to
  // deal again short of reloading — impossible in a home-screen web app.
  const handleWinnerCta = async () => {
    if (isHost) {
      if (!dealable) return; // button is disabled; rebuy someone first
      const shown = winnerInfo;
      if (shown?.ts != null) dismissedWinnerRef.current = winnerInfoDismissKey(shown);
      setWinnerIds([]);
      setWinnerWinnings({});
      setIsDealing(true);
      const dealt = await handleNewHand();
      setIsDealing(false);
      if (!dealt && shown && Array.isArray(shown.winnerIds) && shown.winnerIds.length > 0) {
        // Deal failed: put the hand result (and its Deal button) back.
        dismissedWinnerRef.current = null;
        setWinnerIds(shown.winnerIds);
        setWinnerWinnings(shown.winnerWinnings || {});
      }
      return;
    }
    if (winnerInfo?.ts != null) {
      dismissedWinnerRef.current = winnerInfoDismissKey(winnerInfo);
    }
    setWinnerIds([]);
    setWinnerWinnings({});
  };

  // The pending undo, written by whoever acted. Only offered to the host, and
  // only while it still belongs to the hand being played.
  const undoSnapshot = gameState?.undoSnapshot;
  const canUndo = isHost
    && !!undoSnapshot?.description
    && undoSnapshot.handNumber === handNumber;

  const handleUndoLastAction = async () => {
    if (!canUndo || isUndoing) return;
    setIsUndoing(true);
    try {
      // Pass the ts the host actually saw, so a action landing between opening
      // this dialog and confirming aborts instead of reverting two actions.
      const outcome = await undoLastAction(roomCode, currentPlayerId, undoSnapshot.ts);
      if (!outcome.ok) {
        alert(localizeError(outcome.reason));
      } else {
        console.log(`↩️ Undid: ${outcome.description}`);
      }
      setShowUndoModal(false);
    } catch (error) {
      console.error('Failed to undo:', error);
      alert(t('error.couldNotUndo'));
    } finally {
      setIsUndoing(false);
    }
  };

  // Live before/after for BOTH sides of a move, so the host sees the whole
  // transfer rather than half of it. Null until there is something to show;
  // `insufficient` blocks a commit the source can't cover.
  const movePreview = (() => {
    const from = playersArray.find(p => p.id === moveFromId);
    const to = playersArray.find(p => p.id === moveToId);
    if (!from || !to || from.id === to.id || !moveAmount) return null;

    let amount;
    try {
      amount = parseCurrencyInput(moveAmount);
    } catch {
      return null;
    }
    if (!amount || amount <= 0) return null;

    return {
      amount,
      insufficient: from.stack < amount,
      rows: [
        { name: from.name, before: from.stack, after: from.stack - amount },
        { name: to.name, before: to.stack, after: to.stack + amount },
      ],
    };
  })();

  // Resolve winner names (supports split pots).
  const winnerNames = winnerIds.map(id => playersArray.find(p => p.id === id)?.name ?? t('common.player'));
  const isSplit = winnerIds.length > 1;
  const iWon = winnerIds.includes(currentPlayerId);
  const winnerActive = winnerIds.length > 0;

  // Table Vibe win line — deterministic (seeded by winnerInfo.ts) so every
  // client shows the same sentence. Presentation only; falls back to Classic.
  const tableVibe = settings?.tableVibe || DEFAULT_TABLE_VIBE;
  const winnerNamesJoined = joinWinnerNames(winnerNames);
  const winMessage = getWinMessage(tableVibe, winnerNames, winnerInfo?.ts ?? 0);

  // Callouts — optional winner→player message. Synced via winnerInfo.callout
  // as {mode, index, targetName}; each client renders the text locally.
  const calloutMode = settings?.callouts || DEFAULT_CALLOUT_MODE;
  const syncedCallout = winnerInfo?.callout || null;
  const calloutText = syncedCallout
    ? getCalloutText(syncedCallout.mode, syncedCallout.index, syncedCallout.targetName)
    : null;
  // Picker rules: callouts enabled, exactly one winner (split pots hidden for
  // MVP), and this device belongs to the winner.
  const canSendCallout = calloutMode !== CALLOUT_MODES.OFF && winnerIds.length === 1 && iWon;
  const calloutTargets = tablePlayers.filter(p => p.id !== currentPlayerId);

  const handleSendCallout = async (targetId) => {
    if (!canSendCallout || targetId === currentPlayerId) return; // never target yourself
    const target = playersArray.find(p => p.id === targetId);
    if (!target) return;
    await sendWinnerCallout(roomCode, {
      fromId: currentPlayerId,
      targetId,
      targetName: target.name || t('common.player'),
      mode: calloutMode,
      index: pickCalloutIndex(calloutMode),
      ts: Date.now()
    });
  };

  // Post-dismissal waiting state: winnerInfo is still live in Firebase (hand
  // over, next hand not dealt) but this non-host client closed the modal via
  // "Got it" (winnerActive false). Clears automatically when the host deals —
  // handleNewHand sets winnerInfo: null for everyone. Requires winnerIds so a
  // malformed winnerInfo (e.g. callout-only remnant) can never show it.
  const waitingForHost =
    !isHost &&
    Array.isArray(winnerInfo?.winnerIds) &&
    winnerInfo.winnerIds.length > 0 &&
    !winnerActive;

  // Host safety net: the hand is over in Firebase but this device isn't
  // showing the hand-result overlay. The action slot then carries Deal next
  // hand itself, so the post-hand banner always has a button under it.
  const hostDealFallback =
    isHost &&
    Array.isArray(winnerInfo?.winnerIds) &&
    winnerInfo.winnerIds.length > 0 &&
    !winnerActive &&
    !isDealing;

  // Live award preview in the picker — selected rows show the exact amounts
  // buildWinnerAward will commit, so there is no second confirmation screen.
  const winnerAwardPreview = (showWinnerModal && selectedWinners.length > 0)
    ? buildWinnerAward(selectedWinners)
    : null;

  // The single props contract both layouts receive. Assembled once here so a
  // new control can never exist on one layout and not the other.
  const view = {
    // room + hand state
    gameState, game, settings, players, playersArray: tablePlayers, currentPlayerId, currentPlayer,
    hostId, isHost, handNumber, currentStage, totalPot, currentPots, toCall,
    roundActions, winnerIds, waitingForHost, canDeclareWinner,
    soundMuted, onToggleSound: toggleSoundMuted,
    tapWinnerMode, pickedWinnerIds: selectedWinners, awardLabel,
    awardEnabled: selectedWinners.length > 0,
    onToggleWinnerSeat: toggleWinnerSelection, onConfirmWinner: handleConfirmWinner,
    hostDealFallback, canDealNextHand: dealable, onDealNextHand: handleWinnerCta,
    dealBlockedByAway: blockedByAway,
    pendingRebuys, defaultBuyInLabel,
    onRequestRebuy: handleRequestRebuy, onCancelRebuy: handleCancelRebuy,
    onAddRebuy: handleAddPendingRebuy, onSpectate: handleSpectate,
    onReopenRebuy: () => setRebuyAskOpen(true),
    rebuyAskOpen, requestingRebuy, cancellingRebuy, addingRebuyId,
    canUndo, undoSnapshot,

    // betting inputs
    minRaise, potRaise, allInAmount, raiseAmount, setRaiseAmount,
    isPerformingAction, isDeclaringWinner,

    // player actions
    onFold: handleFold,
    onCall: handleCall,
    onRaise: handleRaise,
    onAllIn: handleAllIn,
    onShowRaisePanel: handleShowRaisePanel,
    checkFoldArmed,
    onToggleCheckFold,

    // host actions
    onNewHand: handleNewHand,
    onDeclareWinner: handleDeclareWinner,
    onAdvanceStage: handleAdvanceStage,
    onEndGame: handleEndGame,

    // modal openers
    onOpenBalances: () => setShowBalancesModal(true),
    onOpenRebuy: () => setShowRebuyModal(true),
    onOpenUndo: () => setShowUndoModal(true),
    onOpenMoveChips: () => setShowMoveChipsModal(true),
    onOpenFeedback: () => setShowFeedbackModal(true),
    onLeaveTable: handleLeaveTable,
    onSitOut: canSitOut ? () => setShowSitOutModal(true) : undefined,
    onImBack: (isAway(currentPlayer) || isSitOutPending(currentPlayer)) ? handleImBack : undefined,
    awayPlayersPayBlinds: payAwayBlinds,
    onToggleAwayPlayersPayBlinds: isHost ? handleToggleAwayPlayersPayBlinds : undefined,
    onOpenRemovePlayer: isHost ? () => setShowRemoveModal(true) : undefined,
    onOpenAddOrRejoin: isHost ? () => { setRejoinError(''); setShowAddOrRejoin(true); } : undefined,
    onOpenChangeBlinds: isHost ? () => setShowChangeBlindsModal(true) : undefined,
    onOpenMakeHost: isHost ? () => setShowMakeHostModal(true) : undefined,
    toolsOpen: showHostTools,
    onToolsOpenChange: setShowHostTools,

    // layout preference (per device)
    layout,
    onToggleLayout: handleToggleLayout,
  };

  return (
    <div
      className={`bg-cream text-ink overflow-hidden flex flex-col ${pinToKeyboard ? '' : 'h-[100dvh]'}`}
      style={pinToKeyboard ? {
        height: vv.height,
        position: 'fixed',
        top: vv.offsetTop,
        left: 0,
        right: 0,
        width: '100%',
      } : undefined}
      data-testid="game-board"
    >

      {isHost && (
        <AddOrRejoinSheet
          open={showAddOrRejoin}
          roomCode={roomCode}
          droppedPlayers={droppedPlayers}
          rejoinLink={rejoinLink}
          rejoinError={rejoinError}
          onPickRejoin={handlePickRejoin}
          onBack={() => {
            setShowAddOrRejoin(false);
            setRejoinLink(null);
            setRejoinError('');
          }}
          onClose={() => {
            setShowAddOrRejoin(false);
            setShowHostTools(false);
            setRejoinLink(null);
            setRejoinError('');
          }}
        />
      )}


      {/* One container, two arrangements. Every piece of state, every handler
          and every modal below stays here, so the layouts cannot diverge on
          behaviour — they render the same shared components over a different
          frame. `layout` is a per-device preference, never room state. */}
      {layout === GAME_LAYOUTS.TABLE ? <TableLayout {...view} /> : <ListLayout {...view} />}

      {/* Mid-game feedback — shared modal, local state only. Elevated above
          the winner overlay (z-60) so a hand ending mid-typing doesn't bury it. */}
      {showFeedbackModal && (
        <div style={{ position: 'relative', zIndex: 70 }}>
          <FeedbackModal onClose={() => setShowFeedbackModal(false)} source="game_board" />
        </div>
      )}

      {/* Undo confirmation — an undo rewrites everyone's screen, so it gets the
          same deliberate second tap that Settle Up does. */}
      <div style={{ position: 'relative', zIndex: showUndoModal ? 70 : 'auto' }}>
      {/* Closes itself the moment there is nothing left to undo, so a completed
          undo never flashes an empty state on its way out. */}
      <Modal
        open={showUndoModal && canUndo}
        onClose={() => setShowUndoModal(false)}
        title={canUndo ? t('game.undoTitle', { action: undoSnapshot.description }) : ''}
      >
        <p className="text-[14px] text-mute mb-6">
          {t('game.undoBody')}
        </p>
        <div className="flex gap-2">
          <button
            onClick={() => setShowUndoModal(false)}
            className="flex-1 h-12 rounded-[12px] border border-line bg-transparent text-ink text-[14px] font-medium"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={handleUndoLastAction}
            disabled={!canUndo || isUndoing}
            className="flex-1 h-12 rounded-[12px] bg-ink text-cream text-[14px] font-semibold disabled:opacity-40"
          >
            {isUndoing ? t('game.undoing') : t('game.undo')}
          </button>
        </div>
      </Modal>
      </div>

      {/* Declare Winner Modal — elevated above gameplay (z-70) */}
      <div style={{ position: 'relative', zIndex: showWinnerModal ? 70 : 'auto' }}>
      <Modal
        open={showWinnerModal}
        onClose={closeWinnerModal}
        title={t('game.declareTitle')}
        testId="declare-winner"
      >
        {/* Pot total */}
        <div className="border border-line rounded-xl px-4 py-3 mb-4 text-center">
          <div className="text-[26px] font-medium tracking-[-0.03em] tnum text-ink">{formatCurrency(totalPot)}</div>
          <div className="label-eyebrow mt-0.5">{(currentPots?.length ?? 0) > 1 ? t('game.nPots', { count: currentPots.length }) : t('game.totalPot')}</div>
        </div>
        <p className="text-[13px] text-mute mb-3">{t('game.selectWinners')}</p>
        <div className="space-y-2 max-h-48 overflow-y-auto mb-4">
          {eligiblePlayers.map(player => {
            const sel = selectedWinners.includes(player.id);
            const won = sel ? (winnerAwardPreview?.playerWinnings?.[player.id] ?? 0) : null;
            return (
              <div
                key={player.id}
                onClick={() => { if (!isDeclaringWinner) toggleWinnerSelection(player.id); }}
                data-testid={`winner-pick-${player.name}`}
                className={`flex items-center justify-between px-4 py-3 rounded-[12px] border cursor-pointer transition-all ${
                  sel ? 'border-ink bg-cream-2' : 'border-line'
                }`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <span className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                    sel ? 'border-ink bg-ink' : 'border-line'
                  }`}>
                    {sel && <span className="w-1.5 h-1.5 bg-cream rounded-full" />}
                  </span>
                  <span className="text-[15px] font-semibold text-ink truncate">{player.name}</span>
                </div>
                <span className={`text-[13px] tnum flex-shrink-0 ml-3 ${sel ? 'font-semibold text-ink' : 'text-mute'}`}>
                  {sel ? t('game.winsAmount', { amount: formatCurrency(won) }) : formatCurrency(player.stack)}
                </span>
              </div>
            );
          })}
        </div>
        {/* Chips going back to someone who did not win the hand: an uncalled
            wager, which is their own money and never part of the pot. */}
        {Object.entries(winnerAwardPreview?.refunds ?? {}).map(([id, amount]) => {
          const player = playersArray.find(p => p.id === id);
          return (
            <p key={id} className="text-[13px] text-mute mb-2 text-center tnum">
              {t('game.uncalledReturn', { amount: formatCurrency(amount), name: player?.name ?? t('common.player') })}
            </p>
          );
        })}
        {winnerAwardPreview?.ambiguousOrphan && (
          <p className="text-[13px] text-accent-danger mb-3 text-center">
            {t('game.ambiguousOrphan')}
          </p>
        )}
        <div className="flex gap-2">
          <button
            onClick={closeWinnerModal}
            disabled={isDeclaringWinner}
            className="flex-1 min-w-0 h-12 rounded-[12px] border border-line bg-transparent text-ink text-[14px] font-medium disabled:opacity-40"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={handleConfirmWinner}
            disabled={selectedWinners.length === 0 || isDeclaringWinner}
            className="flex-1 min-w-0 h-12 px-3 rounded-[12px] bg-ink text-cream text-[14px] font-semibold disabled:opacity-40 truncate"
            data-testid="award-winner"
          >
            {isDeclaringWinner ? t('game.awarding') : t('game.award')}
          </button>
        </div>
      </Modal>
      </div>

      {/* Rebuy Modal — elevated above winner modal (z-70) */}
      <div style={{ position: 'relative', zIndex: showRebuyModal ? 70 : 'auto' }}>
      <Modal
        open={showRebuyModal}
        onClose={closeRebuyModal}
        dismissible={!isRebuying}
        title={t('game.playerRebuy')}
      >
        <div className="space-y-4 mb-5">
          <div>
            <label className="label-eyebrow block mb-2">{t('common.player')}</label>
            <select
              value={selectedPlayerId}
              onChange={(e) => setSelectedPlayerId(e.target.value)}
              disabled={isRebuying}
              data-testid="rebuy-player"
              className="w-full border border-line rounded-xl py-3 px-4 text-ink bg-cream text-[15px] focus:border-ink focus:outline-none disabled:opacity-40"
            >
              <option value="">{t('game.choosePlayer')}</option>
              {tablePlayers.map(player => (
                <option key={player.id} value={player.id}>
                  {t('game.playerOption', { name: player.name, amount: formatCurrency(player.stack) })}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="label-eyebrow block mb-2">{t('common.amount')}</label>
            <input
              type="text"
              {...amountInputProps}
              value={chipAmount}
              onChange={(e) => setChipAmount(e.target.value)}
              placeholder={centsToInputString(2500, currency, locale)}
              disabled={isRebuying}
              data-testid="rebuy-amount"
              className="w-full border border-line rounded-xl py-3 px-4 text-ink bg-cream text-[18px] tnum focus:border-ink focus:outline-none disabled:opacity-40"
            />
            <p className="text-[11.5px] text-mute mt-1">{t('game.addToStack')}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={closeRebuyModal}
            disabled={isRebuying}
            className="flex-1 h-12 rounded-[12px] border border-line bg-transparent text-ink text-[14px] font-medium disabled:opacity-40"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={handleRebuy}
            disabled={!selectedPlayerId || !chipAmount || isRebuying}
            data-testid="rebuy-submit"
            className="flex-1 h-12 rounded-[12px] bg-ink text-cream text-[14px] font-semibold disabled:opacity-40"
          >
            {isRebuying ? t('game.adding') : t('game.addChips')}
          </button>
        </div>
      </Modal>
      </div>

      {/* Change blinds — between hands, elevated above winner modal (z-70) */}
      {showChangeBlindsModal && (
        <div style={{ position: 'relative', zIndex: 70 }}>
          <SettingsOverlay
            blindsOnly
            gameState={gameState}
            currentPlayerId={currentPlayerId}
            onClose={() => setShowChangeBlindsModal(false)}
            onToggleAwayPlayersPayBlinds={handleToggleAwayPlayersPayBlinds}
          />
        </div>
      )}


      {/* Move Chips Modal — host fixing a mistake. Deliberately separate from
          rebuys, and always a transfer: chips only move between players, never
          in or out of the game, which is what keeps settle-up balanced. */}
      <div style={{ position: 'relative', zIndex: showMoveChipsModal ? 70 : 'auto' }}>
      <Modal
        open={showMoveChipsModal}
        onClose={closeMoveChipsModal}
        dismissible={!isMovingChips}
        title={t('game.moveChipsTitle')}
      >
        <p className="text-[13px] text-mute mb-4">
          {t('game.moveChipsBody')}
        </p>
        <div className="space-y-4 mb-5">
          <div>
            <label className="label-eyebrow block mb-2">{t('common.from')}</label>
            <select
              value={moveFromId}
              onChange={(e) => setMoveFromId(e.target.value)}
              disabled={isMovingChips}
              className="w-full border border-line rounded-xl py-3 px-4 text-ink bg-cream text-[15px] focus:border-ink focus:outline-none disabled:opacity-40"
            >
              <option value="">{t('game.choosePlayer')}</option>
              {tablePlayers.map(player => (
                <option key={player.id} value={player.id} disabled={player.id === moveToId}>
                  {t('game.playerOption', { name: player.name, amount: formatCurrency(player.stack) })}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="label-eyebrow block mb-2">{t('common.to')}</label>
            <select
              value={moveToId}
              onChange={(e) => setMoveToId(e.target.value)}
              disabled={isMovingChips}
              className="w-full border border-line rounded-xl py-3 px-4 text-ink bg-cream text-[15px] focus:border-ink focus:outline-none disabled:opacity-40"
            >
              <option value="">{t('game.choosePlayer')}</option>
              {tablePlayers.map(player => (
                <option key={player.id} value={player.id} disabled={player.id === moveFromId}>
                  {t('game.playerOption', { name: player.name, amount: formatCurrency(player.stack) })}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="label-eyebrow block mb-2">{t('common.amount')}</label>
            <input
              type="text"
              {...amountInputProps}
              value={moveAmount}
              onChange={(e) => setMoveAmount(e.target.value)}
              placeholder={centsToInputString(2500, currency, locale)}
              disabled={isMovingChips}
              data-testid="move-chips-amount"
              className="w-full border border-line rounded-xl py-3 px-4 text-ink bg-cream text-[18px] tnum focus:border-ink focus:outline-none disabled:opacity-40"
            />
          </div>
          {/* Both sides of the transfer, so the whole change is visible at once. */}
          {movePreview && (
            <div className={`border rounded-xl px-4 py-3 space-y-1.5 ${movePreview.insufficient ? 'border-accent-danger' : 'border-line'}`}>
              {movePreview.rows.map(row => (
                <div key={row.name} className="flex items-center justify-between">
                  <span className="text-[13px] text-mute truncate pr-3">{row.name}</span>
                  <span className="text-[15px] font-medium tnum text-ink whitespace-nowrap">
                    {formatCurrency(row.before)}
                    <span className="text-mute mx-1.5">→</span>
                    {formatCurrency(row.after)}
                  </span>
                </div>
              ))}
              {movePreview.insufficient && (
                <p className="text-[12px] text-accent-danger pt-1">
                  {t('game.doesntHave', { name: movePreview.rows[0].name })}
                </p>
              )}
            </div>
          )}
        </div>
        <div className="flex gap-2">
          <button
            onClick={closeMoveChipsModal}
            disabled={isMovingChips}
            className="flex-1 h-12 rounded-[12px] border border-line bg-transparent text-ink text-[14px] font-medium disabled:opacity-40"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={handleMoveChips}
            disabled={!movePreview || movePreview.insufficient || isMovingChips}
            className="flex-1 h-12 rounded-[12px] bg-ink text-cream text-[14px] font-semibold disabled:opacity-40"
          >
            {isMovingChips
              ? t('game.moving')
              : movePreview && !movePreview.insufficient
                ? t('game.moveAmount', { amount: formatCurrency(movePreview.amount) })
                : t('game.moveChips')}
          </button>
        </div>
      </Modal>
      </div>

      {/* Mid-game balances — read-only peek; does not end the game */}
      <div style={{ position: 'relative', zIndex: showBalancesModal ? 70 : 'auto' }}>
        <BalancesModal
          open={showBalancesModal}
          onClose={() => setShowBalancesModal(false)}
          players={players}
        />
      </div>

      {/* End Game Modal — elevated above gameplay (z-70) */}
      <div style={{ position: 'relative', zIndex: showEndGameModal ? 70 : 'auto' }}>
      <Modal
        open={showEndGameModal}
        onClose={() => setShowEndGameModal(false)}
        title={t('game.endTitle')}
        testId="end-game-modal"
      >
        <p className={`text-[14px] text-mute ${hasUnfinishedHandChips(players) ? 'mb-2' : 'mb-6'}`}>{t('game.endBody')}</p>
        {hasUnfinishedHandChips(players) && (
          <p className="text-[14px] text-mute mb-6">{t('game.endVoid')}</p>
        )}
        <div className="flex gap-2">
          <button
            onClick={() => setShowEndGameModal(false)}
            className="flex-1 h-12 rounded-[12px] border border-line bg-transparent text-ink text-[14px] font-medium"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={confirmEndGame}
            className="flex-1 h-12 rounded-[12px] border border-line bg-transparent text-accent-danger text-[14px] font-semibold"
            data-testid="end-settle-confirm"
          >
            {t('game.endSettle')}
          </button>
        </div>
      </Modal>
      </div>

      {/* Raise Panel */}
              {showRaisePanel && (
          <RaisePanel
            currentPlayer={currentPlayer}
            minRaise={minRaise}
            potAmount={totalPot}
            highestBet={highestBet}
            bigBlind={settings?.bigBlind || 200}
            onConfirm={handleConfirmRaise}
            onPresetRaise={handlePresetRaise}
            onClose={handleCloseRaisePanel}
            isPerformingAction={isPerformingAction}
            playersArray={playersArray}
            currentPlayerId={currentPlayerId}
          />
        )}

      {/* Winner animation */}
      {winnerActive && (
        isHost ? (
          <HostHandResult
            pot={Object.values(winnerWinnings).reduce((s, a) => s + a, 0)}
            isSplit={isSplit}
            winnerNamesJoined={winnerNamesJoined}
            onDeal={handleWinnerCta}
            canDeal={dealable}
            dealBlockedByAway={blockedByAway}
            onOpenHostTools={() => setShowHostTools(true)}
            pendingRebuys={pendingRebuys}
            onAddRebuy={handleAddPendingRebuy}
            addingRebuyId={addingRebuyId}
            hostChipless={isChipless(currentPlayer)}
            hostRequest={myRebuyRequest}
            defaultBuyInLabel={defaultBuyInLabel}
            onRequestRebuy={handleRequestRebuy}
            requestingRebuy={requestingRebuy}
          />
        ) : iWon ? (
          <WinnerModalCard
            amount={winnerWinnings[currentPlayerId] ?? 0}
            isSplit={isSplit}
            onCta={handleWinnerCta}
            winMessage={winMessage}
            winnerNamesJoined={winnerNamesJoined}
            calloutText={calloutText}
            canSendCallout={canSendCallout}
            calloutTargets={calloutTargets}
            onSendCallout={handleSendCallout}
          />
        ) : (
          <WinnerOpponentNotice
            pot={Object.values(winnerWinnings).reduce((s, a) => s + a, 0)}
            onCta={handleWinnerCta}
            winMessage={winMessage}
            winnerNamesJoined={winnerNamesJoined}
            calloutText={calloutText}
          />
        )
      )}

      <Modal
        open={showSitOutModal}
        onClose={() => setShowSitOutModal(false)}
        title={SIT_OUT_COPY.confirmTitle}
        testId="sit-out-confirm"
      >
        <p className="text-[14px] text-mute mb-3">{SIT_OUT_COPY.confirmBody}</p>
        {holdsCardsThisHand(currentPlayer, game?.winnerInfo) && (
          <p className="text-[13.5px] text-ink mb-3" data-testid="sit-out-live-line">
            {SIT_OUT_COPY.confirmLiveLine}
          </p>
        )}
        <p className="text-[13px] text-mute mb-6" data-testid="sit-out-blinds-line">
          {sitOutBlindsConfirmLine(payAwayBlinds)}
        </p>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setShowSitOutModal(false)}
            className="flex-1 h-12 rounded-[12px] border border-line bg-transparent text-ink text-[14px] font-medium"
            data-testid="sit-out-cancel"
          >
            {SIT_OUT_COPY.confirmCancel}
          </button>
          <button
            type="button"
            onClick={handleSitOut}
            className="flex-1 h-12 rounded-[12px] bg-ink text-cream text-[14px] font-semibold"
            data-testid="sit-out-confirm-btn"
          >
            {SIT_OUT_COPY.confirmConfirm}
          </button>
        </div>
      </Modal>

      {/* Leave table — player cashes out in place. Host Leave hands the
          table to the next connected seat instead of End & Settle.
          z-70 sits above HostHandResult (z-60) so a fold-out overlay
          cannot hide Stay / Leave. */}
      <div style={{ position: 'relative', zIndex: showLeaveModal ? 70 : 'auto' }}>
      <Modal
        open={showLeaveModal}
        onClose={() => setShowLeaveModal(false)}
        title={t('game.leaveTitle')}
        zClass="z-[70]"
        testId="leave-game-modal"
      >
        <p className="text-[14px] text-mute mb-6" data-testid="leave-copy">
          {isHost
            ? (nextHost?.name ? t('game.leaveHost', { name: nextHost.name }) : t('game.leaveHostFallback'))
            : t('game.leavePlayer')}
        </p>
        <div className="flex gap-2">
          <button
            onClick={() => setShowLeaveModal(false)}
            className="flex-1 h-12 rounded-[12px] border border-line bg-transparent text-ink text-[14px] font-medium"
            data-testid="leave-stay"
          >
            {t('game.stay')}
          </button>
          <button
            onClick={handleConfirmLeaveTable}
            className="flex-1 h-12 rounded-[12px] bg-accent-danger text-cream text-[14px] font-semibold"
            data-testid="leave-confirm"
          >
            {t('game.leave')}
          </button>
        </div>
      </Modal>
      </div>

      {/* Host remove — pick a player, then confirm. Cashes them out like a
          leave; they can rejoin like any joiner (same stack mid-game). */}
      {isHost && (
        <RemovePlayerPicker
          open={showMakeHostModal}
          title={t('game.makeHost')}
          hint={t('game.makeHostHint')}
          emptyText={t('game.makeHostEmpty')}
          testId="make-host-picker"
          players={removablePlayers}
          onPick={handleTransferHost}
          onClose={() => setShowMakeHostModal(false)}
        />
      )}

      {isHost && (
        <RemovePlayerPicker
          open={showRemoveModal && !removeTarget}
          players={removablePlayers}
          onPick={handlePickRemove}
          onClose={() => setShowRemoveModal(false)}
        />
      )}
      {isHost && (
        <RemovePlayerConfirm
          player={removeTarget}
          busy={isRemoving}
          error={removeError}
          onCancel={handleCancelRemove}
          onConfirm={handleConfirmRemovePlayer}
        />
      )}

    </div>
  );
}

export default GameBoard; 